import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import type { JSX } from "react";

import { ApiError } from "@/lib/api";

/** Reject with a REQUIRED note (spec §11.1). The note is mailed to the
 * applicant verbatim (`ResellerOnboardingMailer.rejected`), so the copy
 * says so. Rejection is terminal in v1. */
export function RejectDialog({
  isOpen,
  companyName,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  companyName: string;
  isSubmitting: boolean;
  error: ApiError | null;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
}): JSX.Element {
  const [note, setNote] = useState("");
  const empty = note.trim() === "";

  const handleClose = (): void => {
    if (isSubmitting) return;
    setNote("");
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    if (empty) return;
    try {
      await onConfirm(note.trim());
      setNote("");
      onClose();
    } catch {
      // The page surfaces the ApiError through `error`; stay open.
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth PaperProps={{ sx: { maxWidth: 520 } }}>
      <DialogTitle>Reject {companyName}?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 14 }}>
          The applicant is emailed your note and can no longer sign in past the onboarding wall. This cannot be undone from here.
        </DialogContentText>
        <TextField
          autoFocus
          multiline
          minRows={3}
          fullWidth
          required
          size="small"
          label="Note to the applicant"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ "data-cy": "reject-note", maxLength: 4000 }}
          sx={{ mt: 2 }}
          helperText="Required. Sent to the applicant as written."
        />
        {error && (
          <Box role="alert" sx={{ mt: 2, borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.4), bgcolor: (t) => alpha(t.palette.error.main, 0.12), px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>{error.message}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={() => void handleConfirm()} disabled={isSubmitting || empty} data-cy="reject-confirm">
          {isSubmitting ? "Rejecting…" : "Reject application"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
